/* CUSTOM_LEGENDCOLOR_BY_CLAUDE (Ryan 2026-08-12) — mirror Frame Color into the optional
   secondary powder-coat dropdowns.

   THE PROBLEM. Several Legend templates carry an optional second powder-coat surface as a
   line-item property rather than a variant option (the machines already burn 2 of Shopify's
   3 option slots on Frame Color x Pad Color). Those dropdowns default to "Please select", and
   a customer who leaves one alone posts nothing at all — so the order, the Shopify admin and
   the PO all go silent on a colour the factory still has to paint. Legend then gets a phone
   call, or guesses. Driving case: SHOP-1923.

   Leaving it blank has exactly one sensible reading: they want a one-tone machine. Either they
   saw the dropdown and decided they were happy, or they never noticed it and assumed the frame
   colour they DID pick covers the whole machine. Both land on the same expectation, which is
   what makes defaulting to the frame colour safe rather than presumptuous.

   ⚠️ WHY THIS DOES NOT TOUCH THE VISIBLE CONTROL (Ryan, 2026-08-12). It would be easy to drive
   custom-select's own selectOption() and show the mirrored colour on the page. Deliberately not
   done: "Please select" is the affordance that tells the customer there is a choice here at all.
   A pre-filled value reads as settled and gets skipped, so mirroring on the PDP would quietly
   cost us the customers who would otherwise have gone two-tone. The page invites the choice;
   the CART confirms the outcome ("Metal Shroud Color: Mars Red Powder Coat"), which is where the
   customer gets their last look anyway. Do not "finish the job" by making this visible.

   HOW IT HOOKS IN. The theme already renders a real hidden <select> for each of these dropdowns
   (main-product.liquid:988-996 passes include_native + native_name to the custom-select snippet),
   named `properties[<label>]` and bound to the product form. Writing its .value is therefore the
   whole mechanism — the theme's own FormData pickup carries it to /cart/add with no interception.

   ⚠️ NO SUBMIT LISTENER, ON PURPOSE. The obvious implementation is to fill the value on submit.
   Don't: custom-legend-stack-upgrade.js already binds a CAPTURE-phase submit handler that calls
   stopImmediatePropagation() and re-submits the form, so a second capture-phase listener on the
   same node is a coin-flip on registration order. Syncing on variant change instead means the
   value is simply already correct whenever any submit happens, including that re-submit. */

(function () {
  'use strict';

  /* Which custom-option dropdowns mirror the frame. Matched on the property label, which the
     theme puts in the native select's name as `properties[<label>]`.

     An explicit list rather than "any dropdown whose options look like the colour chart" —
     the shape-based version would silently opt in any future dropdown that happens to carry
     the same 30 colours, and being wrong there means painting a machine the wrong colour. */
  var MIRROR_LABELS = [
    'Metal Shroud Color',   // legend-fitness-1120-1130
    'Front Plate Color',    // legend-fitness-3221-np2
    'Rear Plate Color',     // legend-fitness-3221-np2
    'Optional Arm Color'    // legend-fitness-leveredge
  ];

  var FRAME_OPTION = 'Frame Color';
  var FLAG = 'legendColorMirrored';

  /* The placeholder option's value is a NON-BREAKING SPACE, not an empty string (verified in
     every template's dropdown_options), so a bare `!value` test misses it. String.trim() is
     the fix and not an accident: ECMAScript's WhiteSpace production includes U+00A0, so this
     covers the placeholder without putting an invisible character in this file. */
  function isUnset(value) {
    return !value || !String(value).trim();
  }

  function labelOf(select) {
    var m = /^properties\[(.+)\]$/.exec(select.getAttribute('name') || '');
    return m ? m[1] : '';
  }

  /* Reads the live frame colour out of the variant picker. Both selector styles are handled
     because the setting is per-template: the 1120 renders "buttons" (radio inputs), others
     render "dropdown" (a custom-select with its own hidden native select). */
  function frameColorIn(scope) {
    if (!scope) return '';
    var picker = scope.querySelector('.option-selector[data-option="' + FRAME_OPTION + '"]');
    if (!picker) return '';
    var checked = picker.querySelector('input.js-option:checked');
    if (checked) return checked.value;
    var native = picker.querySelector('select.custom-select__native');
    return native ? native.value : '';
  }

  function targets() {
    return document.querySelectorAll('select.custom-select__native[name^="properties["]');
  }

  function sync() {
    Array.prototype.forEach.call(targets(), function (select) {
      if (MIRROR_LABELS.indexOf(labelOf(select)) === -1) return;

      // Only ever overwrite the placeholder, or a value we ourselves put there. Once the
      // customer picks a colour, this element is theirs and a later frame change must not
      // stomp it. Re-selecting the blank option clears the flag via the change handler
      // below, which correctly resumes mirroring.
      if (!isUnset(select.value) && select.dataset[FLAG] !== '1') return;

      var frame = frameColorIn(select.closest('.product-info'));
      if (!frame) return;

      // Never post a colour this dropdown does not itself offer. The lists are identical
      // today (all 30 powder coats), but a value Legend cannot build is worse on a PO than
      // no value at all — it looks like a real instruction.
      var offered = Array.prototype.some.call(select.options, function (opt) {
        return opt.value === frame;
      });
      if (!offered) return;

      // Assigning .value deliberately does NOT dispatch 'change' — that is what keeps this
      // distinguishable from a customer's own pick (see the change handler).
      select.value = frame;
      select.dataset[FLAG] = '1';
    });
  }

  /* custom-select.js sets the native value and dispatches a bubbling 'change' when the customer
     picks an option (assets/custom-select.js:341-343). Our own writes above dispatch nothing, so
     any change event reaching here is the customer's. */
  document.addEventListener('change', function (evt) {
    var select = evt.target;
    if (!select || !select.matches || !select.matches('select.custom-select__native')) return;
    if (isUnset(select.value)) delete select.dataset[FLAG];
    else select.dataset[FLAG] = '0';
  });

  /* The custom-option block carries no [data-dynamic-variant-content], so variant-picker.js
     leaves it standing across a variant change (it only replaces marked blocks —
     variant-picker.js:235-241). State and flags therefore survive; we just re-read the frame. */
  document.addEventListener('on:variant:change', sync);

  // Deferred script, so the DOM is already parsed. Covers the initially-selected variant.
  sync();
}());
